import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  titel?: ReactNode;
  beschreibung?: ReactNode;
  /** Aktionsbereich rechts oben im Kopf. */
  aktion?: ReactNode;
  /** Fußzeile, z. B. Summen oder Hinweise. */
  fuss?: ReactNode;
  /** Ohne Innenabstand, wenn der Inhalt eine eigene Liste mitbringt. */
  ohnePolster?: boolean;
  /** Dezente Hover-Anhebung für anklickbare Karten. */
  interaktiv?: boolean;
}

export function Card({
  titel,
  beschreibung,
  aktion,
  fuss,
  ohnePolster = false,
  interaktiv = false,
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-surface shadow-sm',
        'transition-shadow duration-base ease-out',
        interaktiv && 'hover:border-border-strong hover:shadow-md',
        className,
      )}
      {...rest}
    >
      {(titel || aktion || beschreibung) && (
        <div className="flex items-start justify-between gap-4 border-b border-border px-4 py-3">
          <div className="min-w-0">
            {titel && <h2 className="truncate text-base font-semibold text-fg">{titel}</h2>}
            {beschreibung && (
              <p className="mt-1 text-sm leading-relaxed text-fg-muted">{beschreibung}</p>
            )}
          </div>
          {aktion && <div className="shrink-0">{aktion}</div>}
        </div>
      )}

      <div className={cn(!ohnePolster && 'p-4')}>{children}</div>

      {fuss && (
        <div className="border-t border-border bg-surface-2 px-4 py-3 text-sm text-fg-muted">
          {fuss}
        </div>
      )}
    </div>
  );
}

/** Kennzahl in einer Karte: Bezeichnung oben, Wert groß darunter. */
export function Kennzahl({
  bezeichnung,
  wert,
  zusatz,
  ton = 'neutral',
}: {
  bezeichnung: string;
  wert: ReactNode;
  zusatz?: ReactNode;
  ton?: 'neutral' | 'erfolg' | 'warnung';
}) {
  const tonKlasse =
    ton === 'erfolg' ? 'text-success' : ton === 'warnung' ? 'text-warning' : 'text-fg';
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-fg-subtle">{bezeichnung}</p>
      <p className={cn('mt-1 truncate text-xl font-semibold tabellenziffern', tonKlasse)}>{wert}</p>
      {zusatz && <p className="mt-1 text-xs text-fg-muted">{zusatz}</p>}
    </div>
  );
}
